/*
 * ICOFFEE points on every page. snippets/ic-account-core prints #IcPointsConfig and loads this file while points are
 * on (shop metafield icoffee.loyalty_rules). The points themselves live in the ICOFFEE Accounts app (/apps/icoffee).
 *  - Once per visit a signed-in customer's points are fetched: that is when the app gives the welcome bonus, gives
 *    back rewards that ran out unused and lets old points expire. If that changed the balance the cart is redrawn.
 *  - The points card in the cart (snippets/ic-points-card): "Use N points" asks the app for a one-time code for this
 *    customer and puts it on the cart; "Remove" takes it off and gives the points back.
 * Public: window.IcPoints.get(force) resolves with the app's answer (the account page uses it).
 */
(function () {
  'use strict';

  var el = document.getElementById('IcPointsConfig');
  if (!el || window.IcPoints) return;
  var cfg = JSON.parse(el.textContent);
  var t = cfg.strings || {};
  var SYNC_KEY = 'ic:points-synced';
  var SYNC_EVERY = 20 * 60 * 1000;
  var REWARD = /^ICP-/i;
  var pending = null;

  // translations arrive HTML-escaped (they went through `t`); text nodes need them plain
  function plain(s) {
    var area = document.createElement('textarea');
    area.innerHTML = String(s == null ? '' : s);
    return area.value;
  }
  function put(str, values) {
    var out = plain(str);
    Object.keys(values).forEach(function (k) { out = out.split('[' + k + ']').join(values[k]); });
    return out;
  }
  function money(amount) {
    var n = Number(amount);
    return put(cfg.money || '[amount]', { amount: Number.isInteger(n) ? String(n) : n.toFixed(2) });
  }
  function toast(message, tone) {
    if (!message) return;
    if (window.IcAccount && window.IcAccount.toast) window.IcAccount.toast(message, { tone: tone || 'success' });
  }

  function api(route, body) {
    return fetch(cfg.proxy + '/' + route, {
      method: body ? 'POST' : 'GET',
      credentials: 'same-origin',
      headers: body ? { 'Content-Type': 'application/json', Accept: 'application/json' } : { Accept: 'application/json' },
      body: body ? JSON.stringify(body) : undefined,
    }).then(function (res) {
      return res.json().catch(function () { return null; }).then(function (data) {
        if (!res.ok || !data || data.ok === false) {
          var err = new Error((data && data.error) || 'http_' + res.status);
          err.code = (data && data.error) || 'http_' + res.status;
          err.data = data;
          throw err;
        }
        return data;
      });
    });
  }

  function remember() {
    try { sessionStorage.setItem(SYNC_KEY, String(Date.now())); } catch (e) {}
  }
  function syncedRecently() {
    try { return Date.now() - Number(sessionStorage.getItem(SYNC_KEY) || 0) < SYNC_EVERY; } catch (e) { return false; }
  }

  // the cart was drawn by Liquid with the balance it had; when the app moved it, draw the cart again
  function balanceChanged(balance) {
    if (balance === Number(cfg.balance)) return;
    cfg.balance = balance;
    if (window.IcCart && document.querySelector('[data-ic-points-card]')) window.IcCart.refresh();
  }

  // force asks again, unless a request is already on its way (the visit sync and the account page start together)
  var inflight = false;
  function get(force) {
    if (!cfg.signedIn) return Promise.resolve(null);
    if (pending && (!force || inflight)) return pending;
    inflight = true;
    pending = api('loyalty')
      .then(function (data) {
        inflight = false;
        var p = data.points || { enabled: false };
        remember();
        if (p.enabled) {
          if (p.welcomed) toast(put(t.welcomed, { points: (p.rules && p.rules.welcome_points) || '' }));
          balanceChanged(p.balance);
        }
        return p;
      })
      .catch(function (err) {
        inflight = false;
        pending = null;
        throw err;
      });
    return pending;
  }

  /* ---------- the cart's points card ---------- */

  function cartCodes() {
    return fetch(cfg.cartUrl, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        return (cart.discount_codes || []).filter(function (d) { return d.applicable; }).map(function (d) { return d.code; });
      });
  }

  function busy(card, on) {
    if (!card) return;
    card.classList.toggle('is-busy', on);
    if (on) card.setAttribute('aria-busy', 'true');
    else card.removeAttribute('aria-busy');
  }
  function say(card, text) {
    var msg = card && card.querySelector('[data-ic-points-msg]');
    if (!msg) return;
    msg.textContent = text || '';
    msg.hidden = !text;
  }

  function paintSteps(card) {
    var steps = Number(card.dataset.steps);
    var max = Number(card.dataset.max);
    var label = card.querySelector('[data-ic-points-label]');
    if (label) {
      label.textContent = put(card.dataset.template, {
        points: steps * Number(card.dataset.stepPoints),
        amount: put(card.dataset.money, { amount: String(Math.round(steps * Number(card.dataset.stepValue) * 100) / 100) }),
      });
    }
    card.querySelectorAll('[data-ic-points-step]').forEach(function (b) {
      b.disabled = Number(b.dataset.icPointsStep) < 0 ? steps <= 1 : steps >= max;
    });
  }

  function failed(card, err) {
    busy(card, false);
    // the app has no discount permission yet, or the shopper's session ended
    if (err && err.code === 'signed_out' && window.IcAccount) return window.IcAccount.handleError(err);
    say(card, plain(t.error));
  }

  function useStep(card) {
    if (!window.IcCart || !window.IcCart.setCodes) return;
    var steps = Number(card.dataset.steps);
    var reward = null;
    busy(card, true);
    say(card, '');
    api('loyalty/redeem', { steps: steps, subtotal: Number(card.dataset.subtotal) / 100 })
      .then(function (data) {
        reward = data.reward;
        cfg.balance = data.points && data.points.balance;
        return cartCodes();
      })
      .then(function (codes) {
        var keep = codes.filter(function (c) { return !REWARD.test(c); });
        return window.IcCart.setCodes(keep.concat(reward.code)).then(function (cart) {
          var applied = (cart.discount_codes || []).some(function (d) { return d.code.toUpperCase() === reward.code && d.applicable; });
          if (applied) return toast(put(t.applied, { amount: money(reward.amount) }));
          // Shopify didn't take it (the bundle discount doesn't combine with points): give the points back and say why
          return api('loyalty/release', { code: reward.code })
            .catch(function () {})
            .then(function () { return window.IcCart.setCodes(keep); })
            .then(function () { toast(plain(t.blocked), 'error'); });
        });
      })
      .catch(function (err) {
        // a code that was made but never reached the cart goes back at once
        if (reward) api('loyalty/release', { code: reward.code }).catch(function () {});
        failed(card, err);
      });
  }

  function removeReward(card, code) {
    if (!window.IcCart || !window.IcCart.setCodes) return;
    busy(card, true);
    say(card, '');
    // the points come back first, so the cart redraws with them
    api('loyalty/release', { code: code })
      .then(function (data) {
        cfg.balance = data.points && data.points.balance;
        if (data.released) toast(plain(t.back));
      })
      .catch(function () {})
      .then(cartCodes)
      .then(function (codes) {
        return window.IcCart.setCodes(codes.filter(function (c) { return c.toUpperCase() !== code.toUpperCase(); }));
      })
      .catch(function (err) { failed(card, err); });
  }

  document.addEventListener('click', function (e) {
    var card = e.target.closest('[data-ic-points-card]');
    if (!card) return;

    var step = e.target.closest('[data-ic-points-step]');
    if (step && !step.disabled) {
      var next = Number(card.dataset.steps) + Number(step.dataset.icPointsStep);
      card.dataset.steps = String(Math.max(1, Math.min(Number(card.dataset.max), next)));
      paintSteps(card);
      return;
    }
    if (e.target.closest('[data-ic-points-use]')) return useStep(card);

    var remove = e.target.closest('[data-ic-points-remove]');
    if (remove) return removeReward(card, remove.dataset.icPointsRemove);

    if (e.target.closest('[data-ic-points-signin]')) {
      // the sign-in window opens above the page, so the drawer steps aside first
      if (window.IcCart) window.IcCart.close();
      if (window.IcAccount) window.IcAccount.openAuth('signin');
    }
  });

  // once per visit (and again after a while): the app settles welcome bonus, expired rewards and expired points
  if (cfg.signedIn && !syncedRecently()) {
    var start = function () { get().catch(function () {}); };
    if ('requestIdleCallback' in window) window.requestIdleCallback(start, { timeout: 4000 });
    else setTimeout(start, 1500);
  }

  window.IcPoints = { get: get, money: money };
})();
